<template>
    <main class="product-registration-page">
        <v-container class="product-registration-page__container">
            <v-overlay :absolute="true" :value="noOrgInfo">
                <org-overlay></org-overlay>
            </v-overlay>
            <account-settings-header
                :items="links"
                title="Product registration"
                subtitle="Register your devices and manage your organization's product records in one place."
                :icon="mdiBarcode"
            />
            <header class="registration-heading">
                <div>
                    <span class="registration-eyebrow">YOUR ACCOUNT / DEVICES</span>
                    <h2>Your device workspace</h2>
                    <p>Add one device or import a list, then manage your registered products below.</p>
                </div>
                <a class="registration-jump" href="#registered-products">
                    View registered products
                    <v-icon small>{{ mdiArrowDown }}</v-icon>
                </a>
            </header>
            <section class="registration-methods" aria-label="Register products">
                <register-serial-tab
                    :user-is-org-admin="userIsOrgAdmin"
                    :org-list="orgList"
                    :serial-submit-loading="serialSubmitLoading"
                    :successful-registrations="successfulRegistrations"
                    :loading="$apollo.loading"
                    @submit-serial="handleSubmitSerial"
                />
                <upload-file-tab
                    :user-is-org-admin="userIsOrgAdmin"
                    :org-list="orgList"
                    :upload-loading="uploadLoading"
                    :upload-file-name="uploadFileName"
                    :uploaded-sns="uploadedSns"
                    :loading="$apollo.loading"
                    @upload-file="uploadSerialNumbers"
                    @download-format-example="downloadFormatExample"
                />
            </section>
            <section id="registered-products" class="registration-products" aria-label="Registered products">
                <list-registered-products-tab
                    :org-list="orgList"
                    :query-s-m-model="querySMModel"
                    :registered-sns="registeredSns"
                    :loading="$apollo.loading"
                    :user-is-org-admin="userIsOrgAdmin"
                    @list-registered="handleListRegistered"
                    @export-current="exportCurrent"
                    @delete-serials="handleDeleteSerials"
                    @move-serial="handleMoveSerial"
                />
            </section>
            <v-snackbar v-model="snackbar">
                {{ snackbarMessage }}

                <template v-slot:action="{ attrs }">
                    <v-btn text v-bind="attrs" color="white" @click="snackbar = false"> Close </v-btn>
                </template>
            </v-snackbar>
            <v-dialog v-model="seedStock" max-width="600" :fullscreen="$vuetify.breakpoint.xsOnly">
                <v-card dark class="registration-stock-dialog">
                    <header class="registration-stock-dialog__heading">
                        <span class="registration-eyebrow">MAINTENANCE SUPPORT</span>
                        <h2>Keep essential parts close by</h2>
                        <p>Would you like to request seed stock for your organization?</p>
                    </header>
                    <v-card-text>
                        <v-alert outlined dense color="#f0784c" class="registration-stock-dialog__notice">
                            <span
                                >After registering 150 in-warranty serials of the same model, you may request seed
                                stock. Seed stock is a one-time shipment that allows you to keep a small quantity of
                                components at your location for faster maintenance and repairs.
                            </span>
                        </v-alert>
                        <v-radio-group v-model="stockSelection" column>
                            <v-radio
                                color="#f0784c"
                                label="Not now — continue to product registration"
                                :value="false"
                            ></v-radio>
                            <v-radio
                                color="#f0784c"
                                label="Request seed stock — confirm my contact and address"
                                :value="true"
                            ></v-radio>
                        </v-radio-group>
                    </v-card-text>
                    <v-card-actions class="justify-end">
                        <v-btn
                            depressed
                            class="registration-stock-dialog__continue"
                            @click="proceedStock(stockSelection)"
                            >Continue</v-btn
                        >
                    </v-card-actions>
                </v-card>
            </v-dialog>
            <v-dialog v-model="confirmInfo" max-width="960" :fullscreen="$vuetify.breakpoint.xsOnly">
                <confirm-info @confirmed="infoConfirmed" @cancel="confirmInfo = false"></confirm-info>
            </v-dialog>
            <v-dialog v-model="seedStockView" max-width="450" :fullscreen="$vuetify.breakpoint.xsOnly">
                <seed-stock
                    :confirmed-customer="confirmedCustomer"
                    @cancel="seedStockView = false"
                    @submitted="stockSubmitted($event)"
                    @errorSubmit="errorSubmit($event)"
                ></seed-stock>
            </v-dialog>
        </v-container>
    </main>
</template>

<script>
import { navLinks } from '../navigation.js'
import AccountSettingsHeader from '../account_settings_header.vue'
import gql from 'graphql-tag'
import { downloadJsonAsExcel, processExcel } from '@/utils/excel'
import serialsExample from './serials_example.js'
import OrgOverlay from '../../../components/no_org_overlay'
import ConfirmInfo from './confirm_info.vue'
import SeedStock from './seed_stock.vue'
import RegisterSerialTab from './tabs/register_serial.vue'
import UploadFileTab from './tabs/upload_file.vue'
import ListRegisteredProductsTab from './tabs/list_registered_products.vue'
import { verifySerialForRma } from '@/api'
import {
    mdiCheckboxMarkedCircleOutline,
    mdiAccountSupervisorCircle,
    mdiBarcode,
    mdiTag,
    mdiMapMarker,
    mdiArrowRightBoldCircle,
    mdiHistory,
    mdiCloudUploadOutline,
    mdiFileExcelOutline,
    mdiOfficeBuildingOutline,
    mdiFileDocumentOutline,
    mdiFileDownloadOutline,
    mdiArrowDown
} from '@mdi/js'

export default {
    metaInfo: { title: `Your Account`, subTitle: `Account Settings` },
    name: `ProductRegistration`,
    components: {
        AccountSettingsHeader,
        OrgOverlay,
        ConfirmInfo,
        SeedStock,
        RegisterSerialTab,
        UploadFileTab,
        ListRegisteredProductsTab
    },
    apollo: {
        querySMCustomer: {
            query: gql`
                query QuerySMCustomer($customerId: String!) {
                    querySMCustomer(customerId: $customerId) {
                        smConfirmed
                    }
                }
            `,
            variables() {
                return {
                    customerId: this.$auth.user.org.branchId
                }
            },
            update(data) {
                const isOrgUser = !!this.$auth.user.org
                if (data.querySMCustomer && !data.querySMCustomer.smConfirmed) {
                    this.seedStock = isOrgUser
                }
            }
        },
        queryOrg: {
            query: gql`
                query QueryOrg {
                    queryOrg {
                        gssOrgId
                        orgId
                        orgName
                    }
                }
            `,
            update(data) {
                if (!data.queryOrg) {
                    this.noOrgInfo = true
                }
                if (data.queryOrg.orgId && data.queryOrg.orgName) {
                    this.currentOrg = { orgId: data.queryOrg.orgId, organizationName: data.queryOrg.orgName }
                }
            }
        },
        resolveAccountOrganization: {
            query: gql`
                query ResolveAccountOrganization($resolveAccountOrganizationId: String) {
                    resolveAccountOrganization(id: $resolveAccountOrganizationId) {
                        orgId
                        organizationName
                        childrenOrg {
                            orgId
                            organizationName
                        }
                    }
                }
            `,
            skip() {
                return !this.currentOrg || !this.currentOrg.orgId
            },
            variables() {
                return {
                    resolveAccountOrganizationId: this.currentOrg.orgId
                }
            },
            update(data) {
                if (data.resolveAccountOrganization !== null) {
                    this.childrenOrgs = data.resolveAccountOrganization.childrenOrg
                    this.orgList = [this.currentOrg, ...this.childrenOrgs]
                }
            }
        },
        querySMModel: {
            query: gql`
                query querySMModel {
                    querySMModel {
                        model
                        iwQty
                        oowQty
                    }
                }
            `
        },
        queryRegisteredSNList: {
            query: gql`
                query QueryRegisteredSNList($customerId: String, $model: String) {
                    queryRegisteredSNList(customerId: $customerId, model: $model) {
                        sn
                        assetTag
                        location
                        inwarranty
                        registerDate
                        model
                        orgName
                        orgId
                    }
                }
            `,
            async update(data) {
                this.registeredSns = data.queryRegisteredSNList
            }
        }
    },
    data() {
        return {
            mdiArrowDown,
            mdiHistory,
            mdiArrowRightBoldCircle,
            mdiMapMarker,
            mdiTag,
            mdiBarcode,
            mdiAccountSupervisorCircle,
            mdiCheckboxMarkedCircleOutline,
            mdiCloudUploadOutline,
            mdiFileExcelOutline,
            mdiOfficeBuildingOutline,
            mdiFileDocumentOutline,
            mdiFileDownloadOutline,
            snackbar: false,
            snackbarMessage: '',
            serialSubmitLoading: false,
            uploadLoading: false,
            uploadFileName: '',
            confirmedCustomer: null,
            seedStockView: false,
            askAgain: false,
            confirmInfo: false,
            stockSelection: false,
            seedStock: false,
            selectedOrg: '',
            noSNmessage: 'No serials selected',
            serialHeaders: [
                {
                    text: 'Selected Serial #',
                    value: 'sn',
                    align: 'left'
                }
            ],
            validSNs: false,
            orgList: [],
            dialog: false,
            selectedSNs: [],
            currentOrg: {},
            fromOrg: '',
            toOrg: '',
            childrenOrgs: [],
            links: navLinks,
            noOrgInfo: false,
            serialsExample,
            registeredSns: [],
            search: '',
            noItemsMessage: 'No registered serials found.',
            fileInput: null,
            uploadedSns: [],
            successfulRegistrations: [],
            modelsList: []
        }
    },
    computed: {
        userIsOrgAdmin() {
            return (
                this.$auth.user &&
                this.$auth.user.org &&
                this.$auth.user.org.adminUserIds &&
                this.$auth.user.org.adminUserIds.includes(this.$auth.user.id)
            )
        }
    },
    methods: {
        handleSubmitSerial(formData) {
            const { serialNumber, assetTag, location, registerSerialToOrg } = formData
            this.submitSerial(serialNumber, assetTag, location, registerSerialToOrg)
        },
        handleListRegistered(filterData) {
            const { selectedOrg, selectedModel } = filterData
            this.listRegistered(selectedOrg, selectedModel)
        },
        async handleMoveSerial(moveData) {
            const { selectedSNs, fromOrg, toOrg, onComplete } = moveData
            const success = await this.moveSerial(selectedSNs, fromOrg, toOrg)

            if (typeof onComplete === 'function') {
                onComplete(success)
            }
        },
        async verifySerial(serial, org, assetTag, location) {
            this.serialData = {}
            if (serial && serial.length >= 9) {
                this.callingApi = true
                try {
                    const normalizedSerial = serial.toUpperCase().trim()
                    const { data } = await verifySerialForRma({
                        serial: normalizedSerial
                    })

                    const serialData = data.constructor.name === `Object` ? data : {}

                    this.successfulRegistrations.push({
                        ...serialData,
                        org: org,
                        assetTag: assetTag,
                        location: location
                    })
                } catch (error) {
                    console.error(error)
                } finally {
                    this.callingApi = false
                }
            }
        },
        errorSubmit(error) {
            this.snackbar = true
            this.snackbarMessage = error
        },
        stockSubmitted(data) {
            this.seedStockView = !data.data.updateAccountOrganization.success
            this.snackbar = true
            this.snackbarMessage = !data.data.updateAccountOrganization.success
                ? data.data.updateAccountOrganization.error
                : 'Successfully submitted'
        },
        infoConfirmed(customer) {
            this.confirmedCustomer = customer
            this.confirmInfo = false
            this.seedStockView = true
        },
        proceedStock(viewStock) {
            this.seedStock = false
            if (viewStock) {
                this.confirmInfo = true
            } else {
                const input = {
                    smConfirmed: true,
                    confirmedCategories: []
                }
                this.$apollo.mutate({
                    mutation: gql`
                        mutation UpdateAccountOrganization($input: AccountOrganizationInput!, $orgId: String!) {
                            updateAccountOrganization(input: $input, orgId: $orgId) {
                                complete
                                error
                                success
                                uid
                            }
                        }
                    `,
                    variables: {
                        input: input,
                        orgId: this.$auth.user.org.branchId
                    }
                })
            }
        },
        downloadFormatExample() {
            const example = [
                {
                    SERIAL_NUMBER: null,
                    ASSET_TAG: null,
                    LOCATION: null
                }
            ]
            downloadJsonAsExcel(`SerialsUpload`, `Serials`, example)
        },
        async handleDeleteSerials({ selectedSNs, onComplete }) {
            if (!this.userIsOrgAdmin || !selectedSNs.length) {
                if (typeof onComplete === 'function') onComplete(false)
                return
            }
            // The mutation accepts only one organization per request, even for a mixed selection.
            const groups = new Map()
            selectedSNs.forEach((device) => {
                if (!groups.has(device.orgId)) groups.set(device.orgId, [])
                groups.get(device.orgId).push(device)
            })
            const results = await Promise.all(
                Array.from(groups, async ([orgId, devices]) => {
                    try {
                        if (!orgId) throw new Error('Source organization is unavailable.')
                        const { data } = await this.$apollo.mutate({
                            mutation: gql`
                                mutation DeregisterSNs($snList: [String!]!, $orgId: String) {
                                    deregisterSNs(snList: $snList, orgId: $orgId)
                                }
                            `,
                            variables: { snList: devices.map((device) => device.sn), orgId }
                        })
                        if (data.deregisterSNs !== 'success') throw new Error(data.deregisterSNs || 'Deletion failed.')
                        return { devices, success: true }
                    } catch (error) {
                        return { devices, success: false, error: error.message }
                    }
                })
            )
            const failed = results.filter((result) => !result.success)
            const failedDevices = failed.flatMap((result) => result.devices)
            const deletedCount = selectedSNs.length - failedDevices.length
            this.snackbar = true
            this.snackbarMessage = failed.length
                ? `${deletedCount} device(s) deleted. ${failedDevices.length} could not be deleted: ${failed
                      .map((result) => result.error)
                      .join('; ')}`
                : `${deletedCount} device(s) successfully deleted.`
            if (deletedCount) {
                try {
                    await Promise.all([
                        this.$apollo.queries.querySMModel.refetch({}),
                        this.$apollo.queries.queryRegisteredSNList.refetch({})
                    ])
                    await this.$nextTick()
                } catch (error) {
                    this.snackbarMessage += ' Could not refresh the device list. Please reload the page.'
                }
            }
            if (typeof onComplete === 'function') onComplete(!failed.length, failedDevices)
        },
        async moveSerial(selectedSNs, fromOrg, toOrg) {
            let fromOrgId = fromOrg
            if (fromOrg && typeof fromOrg === 'object' && fromOrg.orgId) {
                fromOrgId = fromOrg.orgId
            }
            if (!(selectedSNs.length > 0 && fromOrgId && toOrg)) {
                return false
            }

            const snList = selectedSNs.map((e) => e.sn)
            const snString = snList.join(', ')

            try {
                const data = await this.$apollo.mutate({
                    mutation: gql`
                        mutation MoveSerialNumbers($serialNumbers: [String!]!, $toOrgId: String!, $fromOrgId: String!) {
                            moveSerialNumbers(serialNumbers: $serialNumbers, toOrgId: $toOrgId, fromOrgId: $fromOrgId) {
                                success
                                error
                            }
                        }
                    `,
                    variables: {
                        serialNumbers: snList,
                        toOrgId: toOrg,
                        fromOrgId: fromOrgId
                    }
                })

                if (data.data.moveSerialNumbers.success === true) {
                    this.snackbar = true
                    this.snackbarMessage = `Serial #` + snString + ` successfully moved`
                    this.selectedSNs = []
                    this.selectedOrg = ''
                    this.fromOrg = ''
                    this.toOrg = ''
                    this.search = ''
                    this.$apollo.queries.queryRegisteredSNList.refetch()
                    return true
                }

                this.snackbar = true
                this.snackbarMessage = `Error moving serial # ${snString}: ` + data.data.moveSerialNumbers.error
                return false
            } catch (error) {
                this.snackbar = true
                this.snackbarMessage = `Error moving serial # ${snList} : ` + error
                return false
            }
        },
        async submitSerial(serialNumber, assetTag, location, registerSerialToOrg) {
            if (!serialNumber || !serialNumber.trim()) {
                return
            }

            this.serialSubmitLoading = true
            try {
                const { data } = await this.$apollo.mutate({
                    mutation: gql`
                        mutation RegisterSNs($snList: [SNRegistrationEntry!]!, $orgId: String) {
                            registerSNs(snList: $snList, orgId: $orgId)
                        }
                    `,
                    variables: {
                        snList: [
                            {
                                sn: serialNumber.toUpperCase().trim(),
                                assetTag: assetTag.trim(),
                                location: location.trim()
                            }
                        ],
                        orgId: this.userIsOrgAdmin ? registerSerialToOrg.orgId : null
                    }
                })

                if (data.registerSNs === 'success') {
                    this.snackbar = true
                    this.snackbarMessage = `Serial #` + serialNumber + ` successfully registered`
                    const submittedOrgName =
                        this.userIsOrgAdmin && registerSerialToOrg
                            ? registerSerialToOrg.organizationName
                            : this.$auth.user && this.$auth.user.org
                            ? this.$auth.user.org.name
                            : ''
                    await this.verifySerial(serialNumber, submittedOrgName, assetTag.trim(), location.trim())
                    await Promise.all([
                        this.$apollo.queries.querySMModel.refetch({}),
                        this.$apollo.queries.queryRegisteredSNList.refetch({})
                    ])
                } else {
                    this.snackbar = true
                    this.snackbarMessage = `Error registering serial: ` + data.registerSNs
                }
            } catch (error) {
                this.snackbar = true
                this.snackbarMessage = `Error registering serial ${serialNumber}: ` + error
            } finally {
                this.serialSubmitLoading = false
            }
        },
        listRegistered(customerId, model) {
            this.registeredSns = []
            this.$apollo.queries.queryRegisteredSNList.refetch({ customerId: customerId, model: model })
        },
        exportCurrent() {
            downloadJsonAsExcel(
                `Registered Serials`,
                `Registered Serials`,
                this.registeredSns.map((e) => ({
                    Serial: e.sn,
                    Model: e.model,
                    Organization: e.orgName,
                    AssetTag: e.assetTag,
                    Location: e.location,
                    InWarranty: e.inwarranty,
                    RegisterDate: new Date(e.registerDate).toLocaleString()
                }))
            )
        },
        async confirmUploadedSerials(uploadSerialToOrg) {
            const uploadOrgId = this.userIsOrgAdmin && uploadSerialToOrg ? uploadSerialToOrg : null

            if (this.userIsOrgAdmin) {
                await this.adminSerialUpload(uploadOrgId)
            } else {
                await this.plainSerialUpload(uploadOrgId)
            }
        },
        snackbarConfirmation(message) {
            this.snackbar = true
            this.snackbarMessage = message
        },
        async plainSerialUpload(uploadOrgId) {
            await this.$apollo
                .mutate({
                    mutation: gql`
                        mutation RegisterSNs($snList: [SNRegistrationEntry!]!, $orgId: String) {
                            registerSNs(snList: $snList, orgId: $orgId)
                        }
                    `,
                    variables: {
                        snList: this.uploadedSns,
                        orgId: uploadOrgId
                    }
                })
                .then((data) => {
                    if (data.data.registerSNs === 'success') {
                        this.snackbarConfirmation(this.uploadedSns.length + ` serials successfully registered`)
                        this.$apollo.queries.querySMModel.fetchMore({})
                        this.$apollo.queries.queryRegisteredSNList.fetchMore({})
                    } else {
                        this.snackbarConfirmation('Error registering serials: ' + data.data.registerSNs)
                        this.uploadedSns = []
                    }
                })
                .catch(() => {
                    this.snackbarConfirmation('Error registering serials')
                    this.uploadedSns = []
                })
                .finally(() => {
                    this.uploadLoading = false
                })
        },
        async adminSerialUpload(uploadOrgId) {
            await this.$apollo
                .mutate({
                    mutation: gql`
                        mutation RegisterAndTransferExistingSns($snList: [SNRegistrationEntry!]!, $orgId: String) {
                            registerAndTransferExistingSns(snList: $snList, orgId: $orgId)
                        }
                    `,
                    variables: {
                        snList: this.uploadedSns,
                        orgId: uploadOrgId
                    }
                })
                .then((data) => {
                    if (data.data.registerAndTransferExistingSns === 'success') {
                        this.snackbarConfirmation(this.uploadedSns.length + ` serials successfully registered`)
                        this.$apollo.queries.querySMModel.fetchMore({})
                        this.$apollo.queries.queryRegisteredSNList.fetchMore({})
                    } else {
                        this.snackbarConfirmation(
                            'Error registering serials: ' + data.data.registerAndTransferExistingSns
                        )
                        this.uploadedSns = []
                    }
                })
                .catch(() => {
                    this.snackbarConfirmation('Error registering serials')
                    this.uploadedSns = []
                })
                .finally(() => {
                    this.uploadLoading = false
                })
        },
        uploadSerialNumbers(file, org) {
            if (file) {
                if (this.uploadLoading) {
                    this.snackbarConfirmation('Please select an organization before uploading serials.')
                    return
                }
                this.uploadedSns = []

                this.uploadLoading = true
                this.uploadFileName = file.name
                const reader = new FileReader()
                reader.onload = async (e) => {
                    try {
                        const processedExcelJson = JSON.parse(processExcel(e.target.result))

                        const inner = processedExcelJson[Object.keys(processedExcelJson)[0]]

                        inner.splice(0, 1)

                        const serials = inner.map((row) => ({
                            sn: row[0].toUpperCase(),
                            assetTag: row[1] ? row[1].toString() : ``,
                            location: row[2] ? row[2].toString() : ``
                        }))

                        this.uploadedSns = serials
                        await this.confirmUploadedSerials(org)
                        this.uploadLoading = false
                    } catch (error) {
                        this.snackbarConfirmation('Error reading file. Please double check your file and formatting.')
                        this.uploadedSns = []
                        this.uploadLoading = false
                    }
                }
                reader.onerror = () => {
                    this.uploadLoading = false
                    this.uploadedSns = []
                    this.snackbarConfirmation('Error reading upload file')
                }
                reader.readAsBinaryString(file)
            }
        }
    }
}
</script>

<style scoped>
.product-registration-page {
    position: relative;
    isolation: isolate;
    min-height: 100vh;
    padding: 34px 0 54px;
    color: #f2f4fa;
    background: radial-gradient(ellipse at 88% 0, rgba(219, 81, 32, 0.15), transparent 38rem),
        linear-gradient(145deg, #292c35, #22252d 65%, #2a2d35);
    font-family: 'Inter Tight', 'Roboto', sans-serif;
}
.product-registration-page::before {
    position: fixed;
    z-index: 0;
    inset: 0;
    background-image: url('https://storage.googleapis.com/gusa-assets/army-BG3.png');
    background-position: center;
    background-size: cover;
    content: '';
    filter: grayscale(1) brightness(0.8);
    mix-blend-mode: soft-light;
    opacity: 0.1;
    pointer-events: none;
}
.product-registration-page__container {
    position: relative;
    z-index: 1;
    max-width: 1260px;
}
.registration-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    margin-bottom: 22px;
    padding-left: 17px;
    border-left: 3px solid #db5120;
}
.registration-eyebrow {
    color: #ff9a74;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 1.4px;
}
.registration-heading h2 {
    margin: 5px 0 4px;
    color: #f4f5f8;
    font-size: clamp(24px, 3vw, 32px);
    font-weight: 600;
    letter-spacing: -0.75px;
}
.registration-heading p {
    margin: 0;
    color: #aeb5c3;
    font-size: 12px;
    line-height: 1.6;
}
.registration-jump {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    gap: 8px;
    padding: 9px 12px;
    color: #ffb396;
    border: 1px solid rgba(240, 120, 76, 0.3);
    border-radius: 7px;
    font-size: 11px;
    text-decoration: none;
}
.registration-jump .v-icon {
    color: inherit;
}
.registration-jump:hover {
    background: rgba(219, 81, 32, 0.1);
}
.registration-jump:focus-visible {
    outline: 2px solid #f0784c;
    outline-offset: 3px;
}
.registration-methods {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    align-items: start;
    gap: 20px;
}
.registration-products {
    margin-top: 24px;
    scroll-margin-top: 90px;
}
.registration-stock-dialog.v-card {
    color: #f2f4fa;
    background: #282b33;
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-top: 2px solid #db5120;
    border-radius: 12px;
    font-family: 'Inter Tight', 'Roboto', sans-serif;
}
.registration-stock-dialog__heading {
    padding: 24px 24px 12px;
}
.registration-stock-dialog__heading h2 {
    margin: 7px 0;
    font-size: 24px;
    font-weight: 600;
    letter-spacing: -0.5px;
}
.registration-stock-dialog__heading p {
    margin: 0;
    color: #aeb5c3;
    font-size: 12px;
    line-height: 1.6;
}
.registration-stock-dialog ::v-deep .v-card__text {
    padding: 12px 24px;
}
.registration-stock-dialog__notice {
    font-size: 12px;
    line-height: 1.7;
}
.registration-stock-dialog ::v-deep .v-label {
    color: #d6d9e1;
    font-size: 12px;
    line-height: 1.6;
}
.registration-stock-dialog ::v-deep .v-card__actions {
    padding: 16px 24px 24px;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
}
.registration-stock-dialog__continue.v-btn {
    color: #fff;
    background: linear-gradient(125deg, #e76638, #db5120);
    border-radius: 7px;
    font-size: 12px;
    letter-spacing: 0;
    text-transform: none;
}
.registration-stock-dialog__continue:focus-visible {
    outline: 2px solid #ffb396;
    outline-offset: 3px;
}
@media (max-width: 959px) {
    .registration-methods {
        grid-template-columns: minmax(0, 1fr);
    }
}
@media (max-width: 600px) {
    .product-registration-page {
        padding-top: 20px;
    }
    .registration-heading {
        align-items: flex-start;
        flex-direction: column;
        gap: 12px;
    }
    .registration-stock-dialog.v-card {
        border-radius: 0;
    }
}
</style>
